import { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import {
  Fingerprint,
  LogOut,
  Users,
  Shield,
  Activity,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Loader,
  FileText,
  Eye,
  Mail,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Key,
  AlertTriangle,
  CreditCard,
  Download,
  BookOpen,
  HelpCircle,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import SessionTimer from "@/components/SessionTimer";
import MySubscription from "@/components/MySubscription";
import UpgradeModal from "@/components/UpgradeModal";

// Subscription status banner component
const SubscriptionBanner = ({ status, details, onDismiss }) => {
  if (!status || status === "active") return null;
  
  const bannerConfig = {
    trial: {
      bg: "bg-blue-50 border-blue-200",
      icon: <Clock className="w-5 h-5 text-blue-500" />,
      title: "Trial Period",
      color: "text-blue-800"
    },
    grace: {
      bg: "bg-amber-50 border-amber-300",
      icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
      title: "Grace Period - Renew Soon!",
      color: "text-amber-800"
    },
    expired: {
      bg: "bg-red-50 border-red-300",
      icon: <XCircle className="w-5 h-5 text-red-500" />,
      title: "Subscription Expired",
      color: "text-red-800"
    },
    suspended: {
      bg: "bg-red-100 border-red-400",
      icon: <AlertCircle className="w-5 h-5 text-red-600" />,
      title: "Account Suspended",
      color: "text-red-900"
    }
  };
  
  const config = bannerConfig[status] || bannerConfig.trial;
  
  return (
    <div className={`${config.bg} border rounded-lg p-4 mb-6`} data-testid="subscription-banner">
      <div className="flex items-start gap-3">
        {config.icon}
        <div className="flex-1">
          <h4 className={`font-semibold ${config.color}`}>{config.title}</h4>
          <p className={`text-sm ${config.color} opacity-80`}>
            {details?.message || "Please check your subscription status."}
          </p>
          {details?.days_remaining !== null && details?.days_remaining <= 7 && (
            <p className="text-sm font-medium mt-1">
              {details.days_remaining} days remaining
            </p>
          )}
        </div>
        {(status === "trial" || status === "grace") && (
          <Link
            to="/license"
            className="px-4 py-2 bg-cyan-500 text-white text-sm font-medium rounded-lg hover:bg-cyan-600 transition flex items-center gap-2"
          >
            <CreditCard className="w-4 h-4" />
            Upgrade
          </Link>
        )}
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [generatingCard, setGeneratingCard] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, complete, pending
  const [sortBy, setSortBy] = useState("updated"); // updated, name, status
  const [sortOrder, setSortOrder] = useState("desc");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [upgradeRequiredTier, setUpgradeRequiredTier] = useState(null);
  const navigate = useNavigate();

  // Handler for upgrade prompt from subscription component
  const handleUpgradeClick = (feature, requiredTier) => {
    setUpgradeFeature(feature);
    setUpgradeRequiredTier(requiredTier);
    setUpgradeModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setUpgradeModalOpen(false);
    setUpgradeFeature(null);
    setUpgradeRequiredTier(null);
  };

  useEffect(() => {
    fetchData();
    checkSubscription();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchQuery, statusFilter, sortBy, sortOrder]);

  const checkSubscription = async () => {
    try {
      const response = await axios.post("/license/subscription-check");
      setSubscriptionStatus(response.data.subscription_status);
      setSubscriptionDetails(response.data.details);
    } catch (error) {
      console.error("Failed to check subscription:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [clientsRes, deviceRes] = await Promise.all([
        axios.get("/clients"),
        axios.get("/fingerprint/device-status"),
      ]);

      setClients(clientsRes.data);
      setDeviceStatus(deviceRes.data);

      // Calculate stats
      const totalFingerprints = clientsRes.data.reduce(
        (sum, client) => sum + (client.total_fingerprints || 0),
        0
      );
      const compliantClients = clientsRes.data.filter(
        (c) => c.total_fingerprints >= 10
      ).length;
      const pendingClients = clientsRes.data.filter(
        (c) => c.total_fingerprints > 0 && c.total_fingerprints < 10
      ).length;

      setStats({
        totalClients: clientsRes.data.length,
        totalFingerprints,
        compliantClients,
        pendingClients,
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortClients = () => {
    let result = [...clients];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.first_name?.toLowerCase().includes(query) ||
          c.last_name?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query) ||
          c.organization?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter === "complete") {
      result = result.filter((c) => c.total_fingerprints >= 10);
    } else if (statusFilter === "pending") {
      result = result.filter((c) => c.total_fingerprints > 0 && c.total_fingerprints < 10);
    } else if (statusFilter === "new") {
      result = result.filter((c) => !c.total_fingerprints || c.total_fingerprints === 0);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "name") {
        comparison = `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`
        );
      } else if (sortBy === "status") {
        comparison = (b.total_fingerprints || 0) - (a.total_fingerprints || 0);
      } else if (sortBy === "updated") {
        comparison = new Date(b.updated_at) - new Date(a.updated_at);
      }
      return sortOrder === "asc" ? -comparison : comparison;
    });

    setFilteredClients(result);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleGenerateCard = async (client, cardType) => {
    const cardKey = `${client.id}-${cardType}`;
    setGeneratingCard(cardKey);
    setDownloadProgress(10);

    const progressToast = toast.loading(
      `Generating ${cardType.toUpperCase()} card...`
    );

    try {
      const progressInterval = setInterval(() => {
        setDownloadProgress((prev) => Math.min(prev + 15, 85));
      }, 300);

      const response = await axios.post(
        `/fingerprint/generate-card/${cardType}?client_id=${client.id}`,
        {},
        { responseType: "blob" }
      );

      clearInterval(progressInterval);
      setDownloadProgress(100);

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${client.first_name}_${client.last_name}_${cardType.toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${cardType.toUpperCase()} card downloaded!`, {
        id: progressToast,
      });
    } catch (error) {
      console.error("Failed to generate card:", error);
      let errorMsg = "Failed to generate card";
      if (error.response?.data) {
        const data = error.response.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const json = JSON.parse(text);
            errorMsg = json.detail || errorMsg;
          } catch {
            errorMsg = text || errorMsg;
          }
        } else {
          errorMsg = data.detail || errorMsg;
        }
      }
      toast.error(errorMsg, { id: progressToast });
    } finally {
      setGeneratingCard(null);
      setDownloadProgress(0);
    }
  };

  const getClientStatus = (client) => {
    const prints = client.total_fingerprints || 0;
    if (prints >= 10) {
      return { label: "Complete", color: "emerald", icon: CheckCircle };
    } else if (prints > 0) {
      return { label: `${prints}/10 Prints`, color: "amber", icon: Clock };
    } else {
      return { label: "New", color: "slate", icon: AlertCircle };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="user-dashboard">
      {/* Main Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      {/* Dark overlay for readability */}
      <div className="fixed inset-0 z-0 bg-slate-900/90" />
      
      {/* Content wrapper */}
      <div className="relative z-10">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10 text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/8bf96fc3-b1b9-4153-935e-e0d5c02560cb/images/b5ffd65d20497bfd9681416db86fdb9eea50ae3581cd0d0b7ff660d2e2271341.png"
              alt="FingerprintVS"
              className="w-8 h-8 rounded"
            />
            <h1 className="text-lg font-heading font-semibold">FingerprintVS</h1>
          </div>
          <div className="flex items-center space-x-4">
            <SessionTimer enabled={true} />
            <Link
              to="/license"
              className="flex items-center space-x-1 text-amber-400 hover:text-amber-300 text-sm"
              data-testid="license-link"
              title="License Management"
            >
              <Key className="w-4 h-4" />
            </Link>
            <Link
              to="/profile"
              className="text-blue-200 hover:text-white text-sm"
              data-testid="profile-link"
            >
              {user?.full_name}
            </Link>
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
              >
                Admin
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 text-blue-200 hover:text-white transition text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Subscription Status Banner */}
        <SubscriptionBanner 
          status={subscriptionStatus} 
          details={subscriptionDetails} 
        />
        
        {/* Welcome Section */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-6 text-white mb-6">
          <h2 className="text-xl font-heading font-semibold mb-1">
            Welcome, {user?.full_name}
          </h2>
          <p className="text-blue-200/70 text-sm">
            {user?.organization ? `${user.organization} • ` : ""}
            {user?.role}
          </p>
        </div>

        {/* My Subscription Section */}
        <div className="mb-6">
          <MySubscription onUpgradeClick={handleUpgradeClick} />
        </div>

        {/* Device Status */}
        {deviceStatus && (
          <div
            className={`rounded-lg border p-4 mb-6 backdrop-blur-md ${
              deviceStatus.connected
                ? "bg-green-500/10 border-green-400/30"
                : "bg-amber-500/10 border-amber-400/30"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity
                  className={`w-5 h-5 ${
                    deviceStatus.connected
                      ? "text-green-400"
                      : "text-amber-400"
                  }`}
                />
                <span className="text-sm font-medium text-white">
                  Scanner Status
                </span>
              </div>
              <span
                className={`text-xs font-semibold px-2 py-1 rounded ${
                  deviceStatus.connected
                    ? "bg-green-500/20 text-green-300"
                    : "bg-amber-500/20 text-amber-300"
                }`}
              >
                {deviceStatus.connected ? "HARDWARE CONNECTED" : "SIMULATION MODE"}
              </span>
            </div>
            {deviceStatus.simulation_mode && (
              <p className="text-xs text-amber-300/80 mt-2">
                Suprema RealScan G10 not detected. Using simulation for testing.
              </p>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <Users className="w-8 h-8 text-blue-300/50" />
                <span className="text-2xl font-heading font-bold text-white">
                  {stats.totalClients}
                </span>
              </div>
              <h3 className="text-xs font-medium text-blue-200/70">
                Total Clients
              </h3>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <Fingerprint className="w-8 h-8 text-blue-300/50" />
                <span className="text-2xl font-heading font-bold text-white">
                  {stats.totalFingerprints}
                </span>
              </div>
              <h3 className="text-xs font-medium text-blue-200/70">
                Total Prints
              </h3>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <span className="text-2xl font-heading font-bold text-green-400">
                  {stats.compliantClients}
                </span>
              </div>
              <h3 className="text-xs font-medium text-blue-200/70">
                Complete
              </h3>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <Clock className="w-8 h-8 text-amber-400" />
                <span className="text-2xl font-heading font-bold text-amber-400">
                  {stats.pendingClients}
                </span>
              </div>
              <h3 className="text-xs font-medium text-blue-200/70">
                In Progress
              </h3>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-5 mb-6">
          <h3 className="text-sm font-heading font-semibold text-white mb-3">
            Quick Actions
          </h3>
          <div className="grid md:grid-cols-4 gap-3">
            <Link
              to="/clients"
              className="flex items-center space-x-3 p-3 border border-white/20 rounded hover:border-cyan-400/50 hover:bg-white/5 transition"
            >
              <Users className="w-6 h-6 text-blue-300" />
              <div>
                <div className="font-medium text-white text-sm">
                  Manage Clients
                </div>
                <div className="text-xs text-blue-200/60">
                  View, add, or edit profiles
                </div>
              </div>
            </Link>

            <Link
              to="/capture"
              className="flex items-center space-x-3 p-3 border border-white/20 rounded hover:border-cyan-400/50 hover:bg-white/5 transition"
            >
              <Fingerprint className="w-6 h-6 text-blue-300" />
              <div>
                <div className="font-medium text-white text-sm">
                  Capture Prints
                </div>
                <div className="text-xs text-blue-200/60">
                  Enroll fingerprints
                </div>
              </div>
            </Link>

            <Link
              to="/clients"
              className="flex items-center space-x-3 p-3 border border-white/20 rounded hover:border-cyan-400/50 hover:bg-white/5 transition"
            >
              <Mail className="w-6 h-6 text-cyan-400" />
              <div>
                <div className="font-medium text-white text-sm">
                  Send Intake Link
                </div>
                <div className="text-xs text-blue-200/60">
                  Email form to client
                </div>
              </div>
            </Link>

            <Link
              to="/dashboard/payments"
              className="flex items-center space-x-3 p-3 border border-white/20 rounded hover:border-cyan-400/50 hover:bg-white/5 transition"
              data-testid="payment-history-link"
            >
              <CreditCard className="w-6 h-6 text-emerald-400" />
              <div>
                <div className="font-medium text-white text-sm">
                  Payment History
                </div>
                <div className="text-xs text-blue-200/60">
                  View invoices & receipts
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Client Table */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg">
          <div className="p-5 border-b border-white/10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-sm font-heading font-semibold text-white">
                Client Overview
              </h3>
              <div className="flex flex-col sm:flex-row gap-2">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-3 py-1.5 bg-white/10 border border-white/20 text-white rounded text-sm w-full sm:w-48 focus:outline-none focus:ring-2 focus:ring-cyan-400 placeholder-blue-200/40"
                  />
                </div>
                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 bg-white/10 border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
                >
                  <option value="all">All Status</option>
                  <option value="complete">Complete</option>
                  <option value="pending">In Progress</option>
                  <option value="new">New</option>
                </select>
                {/* Add Client */}
                <Link
                  to="/clients"
                  className="flex items-center justify-center space-x-1 bg-cyan-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-cyan-400 transition"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Client</span>
                </Link>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Loader className="w-8 h-8 text-blue-300 animate-spin mx-auto" />
              <p className="mt-3 text-blue-200/70 text-sm">Loading clients...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-blue-300/30 mx-auto mb-3" />
              <p className="text-blue-200/70 text-sm mb-1">
                {searchQuery || statusFilter !== "all"
                  ? "No clients match your filters"
                  : "No clients yet"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <Link
                  to="/clients"
                  className="inline-block mt-3 bg-slate-800 text-white px-4 py-2 rounded text-sm font-medium hover:bg-slate-900 transition"
                >
                  Add Your First Client
                </Link>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleSort("name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Client</span>
                        {sortBy === "name" && (
                          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Contact
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortBy === "status" && (
                          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleSort("updated")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Last Updated</span>
                        {sortBy === "updated" && (
                          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClients.map((client) => {
                    const status = getClientStatus(client);
                    const StatusIcon = status.icon;
                    return (
                      <tr
                        key={client.id}
                        className="hover:bg-slate-50 transition"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-medium text-sm">
                              {client.first_name?.[0]}
                              {client.last_name?.[0]}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {client.first_name}{" "}
                                {client.middle_name &&
                                client.middle_name !== "NMN"
                                  ? `${client.middle_name.charAt(0)}. `
                                  : ""}
                                {client.last_name}
                              </div>
                              {client.organization && (
                                <div className="text-xs text-slate-500">
                                  {client.organization}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate-600 truncate max-w-[180px]">
                            {client.email || "-"}
                          </div>
                          {client.phone && (
                            <div className="text-xs text-slate-400">
                              {client.phone}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center space-x-1 text-xs font-medium px-2 py-1 rounded-full bg-${status.color}-100 text-${status.color}-700`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            <span>{status.label}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {formatDate(client.updated_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() =>
                                navigate(`/capture?client_id=${client.id}`)
                              }
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Capture Fingerprints"
                            >
                              <Fingerprint className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => navigate(`/clients/${client.id}`)}
                              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition"
                              title="View Profile"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {client.total_fingerprints > 0 && (
                              <>
                                <button
                                  onClick={() =>
                                    handleGenerateCard(client, "fd258")
                                  }
                                  disabled={generatingCard !== null}
                                  className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition disabled:opacity-50"
                                  title="Generate FD-258"
                                >
                                  {generatingCard === `${client.id}-fd258` ? (
                                    <Loader className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <FileText className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleGenerateCard(client, "fd1164")
                                  }
                                  disabled={generatingCard !== null}
                                  className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded transition disabled:opacity-50"
                                  title="Generate FD-1164"
                                >
                                  {generatingCard === `${client.id}-fd1164` ? (
                                    <Loader className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <FileText className="w-4 h-4" />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                          {/* Progress bar */}
                          {generatingCard?.startsWith(client.id) &&
                            downloadProgress > 0 && (
                              <div className="w-full bg-slate-200 rounded-full h-1 mt-2">
                                <div
                                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                                  style={{ width: `${downloadProgress}%` }}
                                ></div>
                              </div>
                            )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Table Footer */}
          {filteredClients.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex justify-between items-center">
              <span>
                Showing {filteredClients.length} of {clients.length} clients
              </span>
              <Link
                to="/clients"
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                View All →
              </Link>
            </div>
          )}
        </div>

        {/* Resources Section */}
        <div className="mt-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-400" />
            Resources & Documentation
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {/* User Manual PDF */}
            <a
              href="/FingerprintVS_User_Manual_v2.3.pdf"
              download
              className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition group"
              data-testid="download-manual-btn"
            >
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Download className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium group-hover:text-blue-300 transition">User Manual (PDF)</p>
                <p className="text-xs text-blue-200/60">v2.3 - January 2026</p>
              </div>
            </a>
            
            {/* Scanner Download */}
            <a
              href="/scanner/download"
              className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition group"
              data-testid="download-scanner-btn"
            >
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-white font-medium group-hover:text-green-300 transition">Scanner App</p>
                <p className="text-xs text-blue-200/60">Windows Desktop</p>
              </div>
            </a>
            
            {/* Help & Support */}
            <a
              href="mailto:support@fingerprintvs.com"
              className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition group"
              data-testid="support-link"
            >
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-white font-medium group-hover:text-purple-300 transition">Get Support</p>
                <p className="text-xs text-blue-200/60">support@fingerprintvs.com</p>
              </div>
            </a>
          </div>
        </div>
      </main>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        onClose={closeUpgradeModal}
        feature={upgradeFeature}
        requiredTier={upgradeRequiredTier}
      />
    </div>
  );
};

export default DashboardPage;
